// The value basis every sales figure is shown at — the "master filter" (owner, 1 Nov 2026), chosen once for every screen
// (packages/model basis.ts; names, wording and rules as in RetailMaxx). Kept in this browser (localStorage) and as the
// person's own setting on the server (idm.pref value.basis), so it follows them to another browser. Every API read carries
// it (api.ts adds ?basis= when it is not the default), so the server works out the figures and its cache keeps one answer
// per setting. Screens that keep their own values say so in one line (BasisKept), and why.
import React from 'react';
import {
  DEFAULT_VALUE_BASIS, KEEPS_OWN_BASIS, NRV_NOT_IND_AS2, VALUE_BASES, VALUE_BASIS, VALUE_BASIS_TIES, valueBasisShort,
  type KeptScreen, type ValueBasis,
} from '@leo/model';
import { ExplainDrawer } from './explain';
import { getValueBasis, onValueBasis, setValueBasis } from './basis-state';
export { getValueBasis, loadValueBasis, onValueBasis, setValueBasis } from './basis-state';

export const useValueBasis = (): ValueBasis => React.useSyncExternalStore(onValueBasis, getValueBasis, getValueBasis);

/** The full description, for the help drawer: what it is, what is in and out, how the three tie together. */
function basisExplain(b: ValueBasis) {
  const w = VALUE_BASIS[b];
  return { key: 'value_basis', title: `Values: ${w.name}`, meaning: `${w.means} ${VALUE_BASIS_TIES}${b === 'nrv' ? ` ${NRV_NOT_IND_AS2}` : ''}`,
    includes: w.includes, excludes: w.excludes,
    source: VALUE_BASES.map(x => ({ text: `${VALUE_BASIS[x].name}${x === DEFAULT_VALUE_BASIS ? ' (the default)' : ''} — ${VALUE_BASIS[x].means} ${valueBasisShort(x)}.${x === 'nrv' ? ` ${NRV_NOT_IND_AS2}` : ''}`, asOf: null })) };
}

/** The three choices, each with what it includes and what it leaves out (owner, 1 Nov 2026: "with a small description of
 *  what is included and excluded in each"); the current one ticked. */
export function BasisOptions({ value, onPick }: { value: ValueBasis; onPick(b: ValueBasis): void }) {
  return (
    <div className="basis-menu" role="menu" aria-label="Values shown at">
      {VALUE_BASES.map(x => (
        <button key={x} type="button" role="menuitemradio" aria-checked={x === value} className={x === value ? 'on' : undefined} data-basis={x} onClick={() => onPick(x)}>
          <b>{x === value ? '✓ ' : ''}{VALUE_BASIS[x].name}{x === DEFAULT_VALUE_BASIS ? <span className="muted"> · default</span> : null}</b>
          <span className="small">{VALUE_BASIS[x].brief}</span>
          <span className="small muted">{valueBasisShort(x)}</span>
        </button>
      ))}
      <div className="small muted basis-menu-note">Discount % is the same on every setting. Your choice is kept for you on every screen and device.</div>
    </div>
  );
}

/** The picker in the filter bar: the setting chosen, a menu of the three (each with what it includes and excludes), and ⓘ
 *  for the full description. Escape or a click outside closes the menu. */
export function BasisPicker({ compact }: { compact?: boolean }) {
  const b = useValueBasis(); const [open, setOpen] = React.useState(false); const [help, setHelp] = React.useState(false);
  const ref = React.useRef<HTMLSpanElement>(null);
  React.useEffect(() => {
    if (!open) return;
    const out = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', out); document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', out); document.removeEventListener('keydown', esc); };
  }, [open]);
  return (
    <span className="fb-basis" data-basis={b} ref={ref}>
      <span className="fb-basis-l">Values</span>
      <button type="button" className="select basis-btn" aria-label={`Values shown at: ${VALUE_BASIS[b].name}`} aria-haspopup="menu" aria-expanded={open}
        title={`${VALUE_BASIS[b].means} ${VALUE_BASIS_TIES}`} onClick={() => setOpen(o => !o)}>{VALUE_BASIS[b].name} ▾</button>
      <button type="button" className="xi" aria-label="What the value settings mean" title="What is included and excluded in each" onClick={() => setHelp(true)}>
        <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true"><circle cx="8" cy="8" r="6.6" fill="none" stroke="currentColor" strokeWidth="1.4" /><path d="M8 7.2v4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /><circle cx="8" cy="4.9" r="0.95" fill="currentColor" /></svg>
      </button>
      {!compact && <span className="fb-basis-d small muted">{valueBasisShort(b)}</span>}
      {open && <BasisOptions value={b} onPick={x => { setOpen(false); if (x !== b) setValueBasis(x, { save: true }); }} />}
      {help && <ExplainDrawer explain={basisExplain(b)} onClose={() => setHelp(false)} />}
    </span>
  );
}

/** One line on a screen whose figures keep their own values whatever is chosen, and why. */
export function BasisKept({ screen, words }: { screen: KeptScreen; /** a sharper line from the API (plan: what the targets are on) */ words?: string | null | undefined }) {
  const k = KEEPS_OWN_BASIS[screen];
  return <p className="basis-kept small muted" data-kept={screen}><span className="tag" title={k.why}>{k.tag}</span> {words ?? k.why}</p>;
}

/** What is not known on the basis ("MRP not known for ₹X of net sales …"), from the API; nothing when everything is known. */
export function BasisUnknown({ note }: { note?: string | null | undefined }) {
  return note ? <p className="callout small basis-unknown" role="note">{note}</p> : null;
}
